import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';

import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import {
  deleteMeAction,
  logoutUser as logoutUserAction,
  updateEmailAction,
  updatePassword as updatePasswordAction,
} from 'client/modules/Auth/AuthActions';
import { getCurrentOrgId, getOrganization, getProfile } from 'client/modules/Auth/AuthReducer';
import { fetchOrganization } from 'client/modules/Organizations/OrganizationActions';
import { checkSubscriptionAction } from 'client/modules/Subscriptions/SubscriptionActions';
import { getSubscription } from 'client/modules/Subscriptions/SubscriptionReducer';
import useActions from 'client/util/useActions';
import PasskeySettings from 'components/PasskeySettings/PasskeySettings';
import PasswordStrengthIndicator, {
  validatePassword,
} from 'components/PasswordStrengthIndicator/PasswordStrengthIndicator';
import TwoFactorSettings from 'components/TwoFactorSettings/TwoFactorSettings';
import HttpStatusCodes from 'http-status-codes';
import React, { useCallback, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

const messages = defineMessages({
  title: {
    id: 'AccountSettings.Title',
    defaultMessage: 'Security settings',
  },
  changePassword: {
    id: 'AccountSettings.ChangePassword',
    defaultMessage: 'Change password',
  },
  changeEmail: {
    id: 'AccountSettings.ChangeEmail',
    defaultMessage: 'Change email',
  },
  deleteAccount: {
    id: 'AccountSettings.DeleteAccount',
    defaultMessage: 'Delete account',
  },
  areYouSure: {
    id: 'AccountSettings.AreYouSure',
    defaultMessage: 'Are you sure you want to permanently delete your account?',
  },
  yes: {
    id: 'AccountSettings.Yes',
    defaultMessage: 'Yes',
  },
  no: {
    id: 'AccountSettings.No',
    defaultMessage: 'No',
  },
  wrongPassword: {
    id: 'AccountSettings.WrongPassword',
    defaultMessage: 'Wrong password',
  },
  irreversibleDetailed: {
    id: 'AccountSettings.IrreversibleDetailed',
    defaultMessage:
      'This action <b>cannot</b> be undone. This will permanently delete your account including surveys, responses, and all associated data.',
  },
});

function AccountSettingsPage() {
  const dispatch = useDispatch();
  const [checkEmailOpen, setCheckEmailOpen] = useState(false);
  const closeCheckEmail = useCallback(() => setCheckEmailOpen(false), [setCheckEmailOpen]);
  const [updatePassword, updateEmail, logoutUser, deleteMe, checkSubscription] = useActions([
    updatePasswordAction,
    updateEmailAction,
    logoutUserAction,
    deleteMeAction,
    checkSubscriptionAction,
  ]);
  const profile = useSelector(getProfile);
  const subscription = useSelector(getSubscription);
  const isProvisioned = !!profile?.provisioned;
  const [email, setEmail] = useState(profile.email);
  const [currentPassword, setCurrentPassword] = useState();
  const [currentPasswordVisible, setcurrentPasswordVisible] = useState(false);
  const [newPassword, setNewPassword] = useState();
  const [newPasswordVisible, setNewPasswordVisible] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const currentOrgId = useSelector(getCurrentOrgId);
  const organization = useSelector(getOrganization);
  const organizationCreatedAt = organization?.createdAt;

  // Load organization data to check 2FA enforcement
  useEffect(() => {
    if (!currentOrgId || organizationCreatedAt !== undefined) {
      return;
    }
    dispatch(fetchOrganization(null, currentOrgId));
  }, [currentOrgId, dispatch, organizationCreatedAt]);

  const open = useCallback(() => setOpen(true), [setOpen]);
  const close = useCallback(() => {
    setOpen(false);
    setDeleteConfirmation('');
  }, [setOpen]);
  const { formatMessage } = useIntl();
  const confirmDelete = useCallback(() => {
    deleteMe().then(() => {
      logoutUser();
    });
  }, [deleteMe, logoutUser]);
  const changeEmail = useCallback(() => {
    updateEmail(email).then(() => setCheckEmailOpen(true));
  }, [updateEmail, email]);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const changePassword = useCallback(() => {
    const strength = validatePassword(newPassword, formatMessage);
    if (!strength.isStrong) {
      setPasswordError(
        formatMessage({
          id: 'AccountSettings.WeakPassword',
          defaultMessage: 'Please choose a stronger password',
        }),
      );
      return;
    }

    setPasswordError('');
    setPasswordSuccess('');
    updatePassword(currentPassword, newPassword)
      .then(() => {
        setPasswordSuccess(
          formatMessage({
            id: 'AccountSettings.PasswordChanged',
            defaultMessage: 'Password changed successfully',
          }),
        );
        setCurrentPassword('');
        setNewPassword('');
      })
      .catch(e => {
        if (e.status === HttpStatusCodes.UNAUTHORIZED) {
          setPasswordError(formatMessage(messages.wrongPassword));
        } else if (e.status === 400 && e.message && e.message.includes('weak')) {
          // Server-side password validation error
          setPasswordError(
            formatMessage({
              id: 'AccountSettings.WeakPasswordServer',
              defaultMessage: 'Password is too weak. Please ensure it meets all requirements.',
            }),
          );
        } else {
          // Generic error fallback
          setPasswordError(e.message || 'An error occurred while changing password');
        }
      });
  }, [updatePassword, currentPassword, newPassword, formatMessage]);
  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);
  return (
    <>
      <Box sx={{ mt: 1, mb: 3 }}>
        <Helmet title={formatMessage(messages.title)} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Card elevation={1}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" component="h3" sx={{ mb: 3 }}>
                {formatMessage(messages.changeEmail)}
              </Typography>
              {isProvisioned && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <FormattedMessage
                    id="AccountSettings.EmailManagedByIdP"
                    defaultMessage="Your email is managed by your identity provider. Contact your administrator to change it."
                  />
                </Alert>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  variant="outlined"
                  label={
                    <FormattedMessage id="AccountSettings.Email" defaultMessage="Email address" />
                  }
                  id="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ maxWidth: 400 }}
                  disabled={isProvisioned}
                />
                <Box>
                  <Button
                    variant="contained"
                    onClick={changeEmail}
                    disabled={isProvisioned || !email || email === profile.email}
                    sx={{ minWidth: 120 }}
                  >
                    {formatMessage(messages.changeEmail)}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card elevation={1}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" component="h3" sx={{ mb: 3 }}>
                {formatMessage(messages.changePassword)}
              </Typography>
              {isProvisioned && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <FormattedMessage
                    id="AccountSettings.PasswordManagedByIdP"
                    defaultMessage="Your password is managed by your identity provider. Use single sign-on to access your account."
                  />
                </Alert>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField
                  error={!!passwordError}
                  helperText={passwordError}
                  type={currentPasswordVisible ? 'text' : 'password'}
                  variant="outlined"
                  label={
                    <FormattedMessage
                      id="AccountSettings.CurrentPassword"
                      defaultMessage="Current password"
                    />
                  }
                  id="currentPassword"
                  value={currentPassword}
                  onChange={e => {
                    setCurrentPassword(e.target.value);
                    if (passwordError) setPasswordError('');
                    if (passwordSuccess) setPasswordSuccess('');
                  }}
                  size="small"
                  sx={{ maxWidth: 400 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setcurrentPasswordVisible(v => !v)}
                          onMouseDown={e => e.preventDefault()}
                          tabIndex={-1}
                          size="small"
                        >
                          {currentPasswordVisible ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  disabled={isProvisioned}
                />
                <TextField
                  type={newPasswordVisible ? 'text' : 'password'}
                  variant="outlined"
                  label={
                    <FormattedMessage
                      id="AccountSettings.NewPassword"
                      defaultMessage="New password"
                    />
                  }
                  id="newPassword"
                  value={newPassword}
                  onChange={e => {
                    const { value } = e.target;
                    setNewPassword(value);

                    if (passwordError) setPasswordError('');
                    if (passwordSuccess) setPasswordSuccess('');
                  }}
                  size="small"
                  sx={{ maxWidth: 400 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setNewPasswordVisible(v => !v)}
                          onMouseDown={e => e.preventDefault()}
                          tabIndex={-1}
                          size="small"
                        >
                          {newPasswordVisible ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  disabled={isProvisioned}
                />

                {/* Password Strength Indicator */}
                <Box sx={{ maxWidth: 400, mt: 1 }}>
                  <PasswordStrengthIndicator password={newPassword} maxWidth={400} marginTop={-2} />
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    onClick={changePassword}
                    disabled={
                      isProvisioned ||
                      !currentPassword ||
                      !newPassword ||
                      !validatePassword(newPassword, formatMessage).isStrong
                    }
                    sx={{ minWidth: 120 }}
                  >
                    {formatMessage(messages.changePassword)}
                  </Button>
                  {passwordSuccess && (
                    <Alert severity="success" sx={{ mt: 3 }}>
                      {passwordSuccess}
                    </Alert>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>

          <TwoFactorSettings />

          <PasskeySettings />

          <Card
            elevation={1}
            sx={{ borderColor: 'error.light', borderWidth: 1, borderStyle: 'solid' }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" component="h3" sx={{ mb: 3, color: 'error.main' }}>
                {formatMessage(messages.deleteAccount)}
              </Typography>
              {!!subscription && (
                <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
                  <FormattedMessage
                    id="AccountSettings.ActiveSubscription"
                    defaultMessage="You can't delete your account while you have an active subscription. Please cancel your subscription first by going to the {link}."
                    values={{
                      link: (
                        <Link to="/subscriptions" style={{ color: 'inherit', fontWeight: 600 }}>
                          <FormattedMessage
                            id="AccountSettings.SubscriptionsPage"
                            defaultMessage="subscriptions page"
                          />
                        </Link>
                      ),
                    }}
                  />
                </Alert>
              )}
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {formatMessage(messages.irreversibleDetailed, {
                  b: chunks => <span>{chunks}</span>,
                })}
              </Typography>
              <Button
                disabled={!!subscription}
                variant="outlined"
                color="error"
                onClick={open}
                startIcon={<DeleteIcon />}
                sx={{ minWidth: 160 }}
              >
                {formatMessage(messages.deleteAccount)}
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Email Verification Dialog */}
      <Dialog
        open={checkEmailOpen}
        onClose={closeCheckEmail}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
          <EmailIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            <FormattedMessage
              id="AccountSettings.VerifyEmail.Title"
              defaultMessage="Check your email"
            />
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 1 }}>
          <DialogContentText sx={{ fontSize: '1rem', color: 'text.primary' }}>
            <FormattedMessage
              id="AccountSettings.VerifyEmail.PleaseCheck"
              defaultMessage="We've sent you an email to {email}. Please click the link in the email to verify your email address."
              values={{
                email: (
                  <Typography component="span" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {email}
                  </Typography>
                ),
              }}
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button onClick={closeCheckEmail} variant="contained" autoFocus sx={{ minWidth: 100 }}>
            <FormattedMessage id="AccountSettings.Ok" defaultMessage="Ok" />
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={isOpen}
        onClose={close}
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{ pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            {formatMessage(messages.areYouSure)}
          </Typography>
          <IconButton onClick={close} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Alert severity="warning" sx={{ mt: 1, mb: 2, borderRadius: 0, fontWeight: 600 }}>
          <FormattedMessage
            id="AccountSettings.WarningBanner"
            defaultMessage="Unexpected bad things will happen if you don't read this!"
          />
        </Alert>
        <DialogContent sx={{ pt: 0, pb: 3 }}>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6, mb: 3 }}>
            {formatMessage(messages.irreversibleDetailed, {
              b: chunks => (
                <Box
                  component="strong"
                  sx={{ fontWeight: 600, color: 'text.primary', textTransform: 'uppercase' }}
                >
                  {chunks}
                </Box>
              ),
            })}
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 0 }}>
            <FormattedMessage
              id="AccountSettings.TypeConfirmation"
              defaultMessage="Please type {confirmText} to confirm:"
              values={{
                confirmText: (
                  <Typography component="strong" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    <FormattedMessage
                      id="AccountSettings.ConfirmationText"
                      defaultMessage="DELETE"
                    />
                  </Typography>
                ),
              }}
            />
          </Typography>

          <TextField
            variant="outlined"
            size="medium"
            fullWidth
            autoFocus
            value={deleteConfirmation}
            onChange={e => setDeleteConfirmation(e.target.value.toUpperCase())}
            placeholder={formatMessage({
              id: 'AccountSettings.ConfirmationText',
              defaultMessage: 'DELETE',
            })}
            sx={{ mb: 3 }}
          />

          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            fullWidth
            disabled={
              deleteConfirmation !==
              formatMessage({ id: 'AccountSettings.ConfirmationText', defaultMessage: 'DELETE' })
            }
            size="large"
          >
            <FormattedMessage
              id="AccountSettings.DeleteButton"
              defaultMessage="I understand, delete my account"
            />
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AccountSettingsPage;
